import { BG_SECTION_COLOR } from '@repo/common';
import type { IBackgroundSettings } from 'types/models/ISectionSettings';

// Lobby2.0 sections with background support (excluding tournaments for now)
const LOBBY2_SECTIONS_WITH_BG_IMAGES = [
    'high_cash',
    'vip_high_cash',
    'bell_link',
    'bell_link_boost',
    'vip_bell_link',
    'vip_bell_link_boost',
    'mystery',
    'progressive',
    'instant',
    'gods_and_kings',
    'vip_gods_and_kings',
    'premium_link',
    'hot_games',
    'cold_games',
    'high_rtp'
];

export interface IBackgroundApplicationSettings {
    hasBackground: boolean;
    backgroundColor?: string;
    backgroundImage?: string;
    applyToContainer: boolean; // Apply to section container (single view)
    applyToBody: boolean;      // Apply to section body (multi view)
}

export class BackgroundService {
    /**
     * Get background settings for a section based on view mode and device type
     */
    static getBackgroundSettings(
        sectionName: string, 
        viewMode: 'single' | 'multi', 
        isMobile: boolean = false,
        assetsHost?: string,
        assetsHash?: string
    ): IBackgroundApplicationSettings {
        const hasBackground = LOBBY2_SECTIONS_WITH_BG_IMAGES.includes(sectionName?.toLowerCase());
        
        if (!hasBackground) {
            return {
                hasBackground: false,
                applyToContainer: false,
                applyToBody: false
            };
        }

        // Determine image type based on view mode and device
        const imageType = isMobile ? 'mobile' : 
                         (viewMode === 'single' ? 'desktop_section' : 'desktop_lobby');

        // Get background color from constants
        const backgroundColor = BG_SECTION_COLOR[sectionName.toLowerCase() as keyof typeof BG_SECTION_COLOR];

        // Generate background image path using the same pattern as Image component
        let backgroundImage = '';
        if (assetsHost) {
            // For CDN environments, only generate URL if hash is available
            // This prevents requests without the hash in the path
            if (assetsHost.includes('cdn') && (!assetsHash || assetsHash === '')) {
                // Don't generate URL until hash is available
                backgroundImage = '';
            } else {
                const rootPath = assetsHash ? `/${assetsHash}/assets/portal` : '/assets/portal';
                const imagePath = `/backgrounds/${sectionName.toLowerCase()}/${imageType}`;
                backgroundImage = `${assetsHost}${rootPath}${imagePath}.webp${assetsHash ? `?hash=${assetsHash}` : ''}`;
            }
        }

        return {
            hasBackground: true,
            backgroundColor,
            backgroundImage,
            applyToContainer: viewMode === 'single',
            applyToBody: viewMode === 'multi'
        };
    }

    /**
     * Generate background settings for ISectionSettings interface
     */
    static generateBackgroundSettingsForSection(sectionName: string): IBackgroundSettings | undefined {
        const hasBackground = LOBBY2_SECTIONS_WITH_BG_IMAGES.includes(sectionName?.toLowerCase());

        if (!hasBackground) {
            return undefined;
        }

        const backgroundColor = BG_SECTION_COLOR[sectionName.toLowerCase() as keyof typeof BG_SECTION_COLOR];
        const basePath = `/assets/portal/backgrounds/${sectionName.toLowerCase()}`;

        return {
            hasBackground: true,
            backgroundColor,
            backgroundImages: {
                desktop_lobby: `${basePath}/desktop_lobby.webp`,
                desktop_section: `${basePath}/desktop_section.webp`,
                mobile: `${basePath}/mobile.webp`
            }
        };
    }

    /**
     * Check if a section has background support
     */
    static hasBackgroundSupport(sectionName: string): boolean {
        return LOBBY2_SECTIONS_WITH_BG_IMAGES.includes(sectionName?.toLowerCase());
    }

    /**
     * Get CSS style object for background application
     */
    static getBackgroundStyles(settings: IBackgroundApplicationSettings): React.CSSProperties {
        if (!settings.hasBackground || !settings.backgroundImage) {
            return {};
        }

        const styles: React.CSSProperties = {};

        if (settings.backgroundColor) {
            styles.backgroundColor = settings.backgroundColor;
        }

        if (settings.backgroundImage) {
            styles.backgroundImage = `url(${settings.backgroundImage})`;
        }

        return styles;
    }
}
